// StationGroupLayout – výběr skupiny stanic (víceúrovňový) + filtry + produkt (Mapa)
// Sidebar se 3 přepínatelnými menu (Skupina/Filtry/Produkty), viz doc/plans/station-group-layout.md
import { useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useLocation, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { adminApi } from '../api/adminApi'
import { normalizeCzech, haversineKm } from '../utils/geo'
import { likeMatch, applyPlusMinusGhId } from '../utils/likeMatch'
import { parseGhIdExpr, evalGhIdExpr } from '../utils/ghIdExpr'
import { StationMap } from '../components/map/StationMap'
import { FiltersPanel } from '../components/filters/FiltersPanel'
import { GroupTree } from '../components/group/GroupTree'
import { GroupConfigurator } from '../components/group/GroupConfigurator'
import { SaveGroupModal } from '../components/group/SaveGroupModal'
import { InfoPanel } from '../components/panels/InfoPanel'
import { GroupPathSelects } from '../components/panels/GroupPathSelects'
import { PanelHeader } from '../components/panels/PanelHeader'
import { UserIcon } from '../components/auth/UserIcon'
import { OnlineModeToggle } from '../components/OnlineModeToggle'
import { useStationGroupStore } from '../store/useStationGroupStore'
import { useAllStations } from '../hooks/useAppData'
import { useStationGroupMembers, useStationGroupsBySources, useSavedStationGroups, useSavedStationGroupFilter } from '../hooks/useStationGroups'
import { buildGroupTree, unionNodeGhIds, findNodeByPath } from '../utils/computeGroupTree'
import { buildGroupColorMap } from '../utils/groupColors'
import { useAuthStore } from '../store/useAuthStore'
import { api } from '../api/client'
import { GROUP_PRODUCTS, findProductNode } from '../config/productRegistry'
import { useTrackProduct } from '../hooks/useTrackProduct'
import filterIcon from '../assets/icons/filter.svg'
import mapIcon from '../assets/icons/map.svg'
import productsIcon from '../assets/icons/products.svg'
import groupIcon from '../assets/icons/group.svg'
import weatherStationIcon from '../assets/weather-station.svg'

// Stejná logika filtrace jako StationSelectLayout.jsx — udrženo zvlášť, aby /select a /station-group na sobě nezávisely.
// Filtr „Skupina" (filters.groupPaths) tady záměrně chybí — výběr skupiny má /station-group vlastní
// (strom / selectedPath), FiltersPanel sem nedostává showGroupFilter, takže groupPaths je vždy prázdné.
function applyFilters(stations, filters) {
  let result = stations
  if (filters.ghId) {
    const { ast, error } = parseGhIdExpr(filters.ghId)
    if (!error && ast) result = result.filter(s => evalGhIdExpr(s.gh_id, ast))
  }
  if (filters.wsi) result = result.filter(s => likeMatch(s.wsi, filters.wsi))
  if (filters.stationTypes.length > 0) result = result.filter(s => filters.stationTypes.includes(s.station_type))
  if (filters.elevationMin) {
    const min = parseFloat(filters.elevationMin)
    if (!isNaN(min)) result = result.filter(s => (s.elevation ?? 0) >= min)
  }
  if (filters.elevationMax) {
    const max = parseFloat(filters.elevationMax)
    if (!isNaN(max)) result = result.filter(s => (s.elevation ?? 0) <= max)
  }
  if (filters.hierarchyPaths.length > 0) {
    result = result.filter(s => {
      const stationPath = [s.h1, s.h2, s.h3, s.h4]
      return filters.hierarchyPaths.some(selectedPath => selectedPath.every((val, idx) => val === stationPath[idx]))
    })
  }
  if (filters.basin.length > 0) result = result.filter(s => s.basin && filters.basin.includes(s.basin))
  if (filters.stationOwners?.length > 0) result = result.filter(s => s.sow_name && filters.stationOwners.includes(s.sow_name))
  if (filters.elAbbreviations.length > 0) {
    const match = filters.elMatchMode === 'all' ? 'every' : 'some'
    const today = new Date().toISOString().split('T')[0]
    const matchesEl = (o, obsType, abbr) =>
      o.obs_type === obsType && o.el_abbreviation === abbr &&
      (!filters.elCurrentOnly || (o.end_date && o.end_date > today))
    result = result.filter(s => {
      if (!s.observation) return false
      return filters.elAbbreviations[match](([obsType, abbr]) =>
        s.observation.some(o => matchesEl(o, obsType, abbr))
      )
    })
  }
  if (filters.activeOnly) {
    const today = new Date().toISOString().split('T')[0]
    result = result.filter(s => s.end_date && s.end_date > today)
  }
  if (filters.name) {
    const q = normalizeCzech(filters.name)
    result = result.filter(s => normalizeCzech(s.name).includes(q))
  }
  if (filters.fullName) {
    const q = normalizeCzech(filters.fullName)
    result = result.filter(s => normalizeCzech(s.full_name).includes(q))
  }
  if (filters.locality && filters.localityRadius) {
    const radius = parseFloat(filters.localityRadius)
    if (!isNaN(radius) && radius > 0) {
      result = result.filter(s => {
        if (s.latitude == null || s.longitude == null) return false
        return haversineKm(filters.locality.lat, filters.locality.lon, s.latitude, s.longitude) <= radius
      })
    }
  }
  // +GH_ID / -GH_ID — vynucené zahrnutí/vyloučení, aplikuje se jako poslední krok bez ohledu na ostatní filtry
  result = applyPlusMinusGhId(result, stations, filters.plusGhId, filters.minusGhId)
  return result
}

export function StationGroupLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const qc = useQueryClient()
  const savedGroupId = location.state?.savedGroupId ?? null
  const user = useAuthStore(s => s.user)
  const isAdmin = user?.is_admin === 'Y'

  const groupLevels = useStationGroupStore(s => s.groupLevels)
  const setGroupLevels = useStationGroupStore(s => s.setGroupLevels)
  const selectedPath = useStationGroupStore(s => s.selectedPath)
  const setSelectedPath = useStationGroupStore(s => s.setSelectedPath)
  const filters = useStationGroupStore(s => s.filters)
  const setFilters = useStationGroupStore(s => s.setFilters)
  const groupFilterBaseline = useStationGroupStore(s => s.groupFilterBaseline)
  const setGroupFilterBaseline = useStationGroupStore(s => s.setGroupFilterBaseline)
  const productFilterBaseline = useStationGroupStore(s => s.productFilterBaseline)
  const setProductFilterBaseline = useStationGroupStore(s => s.setProductFilterBaseline)
  const editingSavedGroupId = useStationGroupStore(s => s.editingSavedGroupId)
  const setEditingSavedGroupId = useStationGroupStore(s => s.setEditingSavedGroupId)
  const resetToBlank = useStationGroupStore(s => s.resetToBlank)
  const rightView = useStationGroupStore(s => s.rightView)
  const setRightView = useStationGroupStore(s => s.setRightView)
  const lastProduct = useStationGroupStore(s => s.activeProduct)
  const setLastProduct = useStationGroupStore(s => s.setActiveProduct)

  // expandedPanel řídí jen doplňkový overlay panel vpravo (null = žádný otevřený) — souhrny
  // (GroupPathSelects/InfoPanel) v levém menu jsou vždy viditelné nezávisle na něm, viz
  // doc/plans/station-group-sidebar-redesign.md sekce III.
  const [expandedPanel, setExpandedPanel] = useState(null)
  // rightView (mapa vs. produkty) je teď v useStationGroupStore, aby přežil refresh —
  // viz doc/plans/station-group-sidebar-redesign.md sekce II.
  const [configuratorOpen, setConfiguratorOpen] = useState(false)
  const [saveModalOpen, setSaveModalOpen] = useState(false)
  const [localityPickMode, setLocalityPickMode] = useState(false)
  const [shrinkBanner, setShrinkBanner] = useState(null)

  const { allStations, isLoading: allStationsLoading } = useAllStations()
  const savedGroups = useSavedStationGroups()

  // Mapa už není produkt s vlastním slugem — bez produktu v URL (/station-group) i na rightView
  // 'map' se napravo zobrazuje mapa, viz rightView níže.
  const activeProduct = params.product || null
  const activeProductNode = findProductNode('station-group', activeProduct)
  const activeProductGroup = GROUP_PRODUCTS.find(g => g.items.some(item => item.slug === activeProduct))?.group ?? null

  // Sledování přístupů (doc/plans/usage-analytics-client.md, etapa K2) — jedno volání
  // pro všechny produkty skupiny stanic. Posílá se, jen když se produkt opravdu vykresluje
  // (vpravo je produktový pohled, ne mapa, a slug existuje v registru).
  // gh_id se neposílá — skupina není jedna stanice a skupina se zatím nesleduje (plán 2.3).
  useTrackProduct(rightView === 'products' && activeProductNode ? activeProduct : null)

  // Poslední produkt držíme ve store, aby přežil odskok na /station/:id a zpět (na home odkazu
  // je jen navigate('/station-group') bez slugu). Při vstupu bez produktu v URL a bez explicitního
  // otevření uložené skupiny (savedGroupId) obnovíme naposledy zvolený produkt do URL.
  // clearProduct() = explicitní „— nevybráno —“: nastaví lastProduct na 'map', takže se produkt
  // neobnoví a redirect zpátky na produkt se nespustí.
  const clearProduct = useCallback(() => {
    setLastProduct('map')
    navigate('/station-group')
  }, [setLastProduct, navigate])
  useEffect(() => {
    if (activeProduct) {
      if (activeProduct !== lastProduct) setLastProduct(activeProduct)
      return
    }
    if (savedGroupId) return // úmyslné čerstvé otevření skupiny — neobnovuj produkt
    if (lastProduct === 'map') return // uživatel zvolil „— nevybráno —“
    // Obnovit produkt jen když je co obnovovat (existuje uložený výběr skupiny) — čistě první
    // návštěva /station-group s prázdným store zůstane na mapě.
    const hasPreservedSelection = editingSavedGroupId != null || groupLevels.length > 0 || selectedPath.length > 0
    if (hasPreservedSelection && lastProduct) {
      navigate(`/station-group/${lastProduct}`, { replace: true })
    }
  }, [activeProduct, savedGroupId]) // eslint-disable-line

  // Baseline definice naposled načtené/uložené skupiny — banner „Upravujete...“ se zobrazí až
  // když se aktuální stav od téhle baseline odchýlí (ne hned po pouhém otevření skupiny).
  // null = zatím se nemá s čím srovnávat (nic nenačteno) nebo baseline čeká na dokreslení po
  // změně state (setGroupLevels/setSelectedPath/setFilters se projeví až v NÁSLEDUJÍCÍM renderu,
  // takže baseline nelze nastavit synchronně ve stejném callbacku, jinak se porovnává proti starému stavu).
  const savedBaselineRef = useRef(null)
  const [baselinePending, setBaselinePending] = useState(false)

  // Vstup s předvyplněnou uloženou skupinou (z LayoutMain nebo z admin editace) — v obou případech
  // jde o otevření existující skupiny, takže se rovnou označí jako editovaná (nabídne se „Přepsat“
  // v SaveGroupModal), ne jen když přijde přes editMode z admin správy.
  const jumpToTab = location.state?.jumpToTab ?? null
  useEffect(() => {
    if (!savedGroupId) return
    api.get(`/saved_station_group?id=${savedGroupId}`).then(rows => {
      const g = rows[0]
      if (!g) return
      setGroupLevels(g.definition.groupLevels || [])
      setSelectedPath([])
      setGroupFilterBaseline(g.group_filter || {})
      setEditingSavedGroupId(g.id)
      if (jumpToTab) setExpandedPanel(jumpToTab)
    })
  }, [savedGroupId]) // eslint-disable-line

  // Doplňkový filtr aktuálního produktu pro editovanou skupinu — znovu se dotáhne při přepnutí
  // skupiny i při přepnutí produktu (activeProduct), viz doc/plans/group-product-filters.md.
  // Pokud existuje filtr uložený k produktu, použije se ten celý (bez sjednocení) — obsahuje
  // typicky i položky původem ze skupiny, protože se ukládá jako kompletní kopie stavu panelu.
  // Jinak se použije filtr skupiny.
  const productFilterRow = useSavedStationGroupFilter(editingSavedGroupId, activeProduct)
  // Po refreshi / odskoku na /station a zpět je stav (skupina/produkt/filtry) obnovený ze
  // sessionStorage. Tenhle efekt dotahuje baseline filtr k editované skupině/produktu — po pouhé
  // obnově se ale nesmí spustit, jinak přepíše obnovené filters. Sledujeme naposledy „vyřízenou“
  // dvojici (skupina, produkt); ref se inicializuje persistovaným stavem, takže restore (včetně
  // redirectu na naposledy zvolený produkt) je no-op. Přepíšeme filters baseline hodnotou teprve
  // při skutečné ZMĚNĚ skupiny/produktu uživatelem.
  // Dvojice (skupina, produkt), pro kterou se filters NEMAJÍ přepisovat — jde o obnovu ze storage.
  // Platí do první skutečné změny skupiny/produktu; pak se vynuluje a efekt zase běží normálně
  // (včetně asynchronního dotažení productFilterRow).
  const restoredKeyRef = useRef(
    `${editingSavedGroupId}::${activeProduct ?? (lastProduct === 'map' ? null : lastProduct)}`
  )
  useEffect(() => {
    if (!editingSavedGroupId || groupFilterBaseline === null) return
    if (!activeProduct) return // mezistav před redirectem na naposledy zvolený produkt
    const key = `${editingSavedGroupId}::${activeProduct}`
    if (key === restoredKeyRef.current) {
      // restore této dvojice → filters nech být, jen dopočítej baseline pro banner „Upravujete…“
      setBaselinePending(true)
      return
    }
    restoredKeyRef.current = null // od teď je každá další změna skutečná
    setProductFilterBaseline(productFilterRow?.filter ?? null)
    setFilters(productFilterRow?.filter ?? groupFilterBaseline)
    setBaselinePending(true)
  }, [editingSavedGroupId, activeProduct, groupFilterBaseline, productFilterRow]) // eslint-disable-line

  // Sesbírej station_group_source.id napříč statickými úrovněmi — potřebné k resolvnutí uložených
  // memberCodes na aktuální station_group.id (id se liší mezi postgres/oracle větví, code je stabilní).
  const staticSourceIds = useMemo(() => {
    const ids = new Set()
    for (const level of groupLevels) {
      if (level.dynamicDefType || level.sourceId == null) continue
      ids.add(level.sourceId)
    }
    return [...ids]
  }, [groupLevels])

  const { data: sourceGroups, isReady: sourceGroupsReady } = useStationGroupsBySources(staticSourceIds, { withStatus: true })
  // Map<`${sourceId}:${code}`, station_group.id> pro aktuální datasource větev uživatele
  const codeToId = useMemo(() => {
    const map = new Map()
    for (const g of sourceGroups) map.set(`${g.source_id}:${g.code}`, g.id)
    return map
  }, [sourceGroups])

  // Resolvni memberCodes → aktuální station_group.id pro dotažení členství
  const staticMemberIds = useMemo(() => {
    const ids = new Set()
    for (const level of groupLevels) {
      if (level.dynamicDefType) continue
      for (const bucket of level.buckets) {
        for (const code of bucket.memberCodes) {
          const id = codeToId.get(`${level.sourceId}:${code}`)
          if (id != null) ids.add(id)
        }
      }
    }
    return [...ids]
  }, [groupLevels, codeToId])

  const { data: memberRows, isReady: memberRowsReady } = useStationGroupMembers(staticMemberIds, { withStatus: true })
  const memberGhIds = useMemo(() => {
    const map = new Map()
    for (const row of memberRows) {
      if (!map.has(row.group_id)) map.set(row.group_id, [])
      map.get(row.group_id).push(row.gh_id)
    }
    return map
  }, [memberRows])

  // Filtrovaná množina stanic — strom (včetně dynamického clusteringu) se staví přímo z ní, ne až
  // se filtruje výsledný strom, aby clustery (např. "10 stanic") odpovídaly počtu po filtraci.
  const filteredStations = useMemo(() => applyFilters(allStations, filters), [allStations, filters])
  const filteredGhIdSet = useMemo(() => new Set(filteredStations.map(s => s.gh_id)), [filteredStations])
  const stationsByGhId = useMemo(() => new Map(filteredStations.map(s => [s.gh_id, s])), [filteredStations])

  // Statické buckety omezíme na filtrovanou množinu ještě před stavbou stromu (dynamické úrovně
  // dostávají filtrovanou vstupní množinu přímo přes stationsByGhId v buildGroupTree).
  const filteredMemberGhIds = useMemo(() => {
    const map = new Map()
    for (const [groupId, ghIds] of memberGhIds) {
      map.set(groupId, ghIds.filter(gh => filteredGhIdSet.has(gh)))
    }
    return map
  }, [memberGhIds, filteredGhIdSet])

  const filteredTree = useMemo(
    () => buildGroupTree(groupLevels, filteredMemberGhIds, stationsByGhId, codeToId),
    [groupLevels, filteredMemberGhIds, stationsByGhId, codeToId]
  )

  // Strom je „hotový“ (a smysluplně prohledatelný) teprve až doběhnou VŠECHNY vstupní dotazy.
  // Po refreshi se stav (včetně víceúrovňového selectedPath) obnoví ze storage dřív, než dorazí
  // member/source data — do té doby by prune efekt níže neviděl hlubší uzly a zbytečně by uřízl
  // selectedPath na 1. úroveň. Proto ho spustíme až po tomhle příznaku.
  const treeReady = !allStationsLoading && sourceGroupsReady && memberRowsReady

  // Zmizení vybraného uzlu po zpřísnění filtrů → pád na rodiče / na "všechny stanice" (viz I.1.8 poznámka)
  useEffect(() => {
    if (!treeReady) return
    if (selectedPath.length === 0) return
    if (findNodeByPath(filteredTree, selectedPath)) return
    if (selectedPath.length > 1) {
      const parentPath = selectedPath.slice(0, -1)
      if (findNodeByPath(filteredTree, parentPath)) {
        setSelectedPath(parentPath)
        setShrinkBanner('Výběr byl zúžen filtry na nadřazenou skupinu')
        return
      }
    }
    setSelectedPath([])
    setShrinkBanner('Výběr byl zúžen filtry na všechny stanice')
  }, [filteredTree, treeReady]) // eslint-disable-line

  useEffect(() => {
    if (!shrinkBanner) return
    const t = setTimeout(() => setShrinkBanner(null), 5000)
    return () => clearTimeout(t)
  }, [shrinkBanner])

  const selectedNode = useMemo(() => findNodeByPath(filteredTree, selectedPath), [filteredTree, selectedPath])
  // Na kořeni (selectedPath = [], „Všechny stanice“) = sjednocení všech kořenových uzlů stromu (jen vybrané podskupiny
  // z konfigurátoru), ne úplně všechny stanice — pokud groupLevels nejsou nastavené vůbec (tree je prázdný), zůstává null.
  const groupGhIds = useMemo(() => {
    if (selectedNode) return unionNodeGhIds(selectedNode)
    if (selectedPath.length === 0 && filteredTree.length > 0) {
      const result = new Set()
      for (const node of filteredTree) for (const gh of unionNodeGhIds(node)) result.add(gh)
      return result
    }
    // Skupina je nakonfigurovaná, ale filtr z ní nenechal žádnou stanici (prázdný filteredTree)
    // → výsledek má být prázdný, ne „všechny stanice" (jinak by filtr station_type=ASNS ukázal
    // úplně všechny ASNS stanice v ČR bez ohledu na skupinu).
    if (groupLevels.length > 0) return new Set()
    return null
  }, [selectedNode, selectedPath, filteredTree, groupLevels])

  const effectiveStations = useMemo(() => {
    const base = groupGhIds ? allStations.filter(s => groupGhIds.has(s.gh_id)) : allStations
    return applyFilters(base, filters)
  }, [allStations, groupGhIds, filters])

  // Barevný mód „Podle skupiny“ (sekce III): list = 1 barva, uzel s dětmi = barva na přímé dítě + legenda.
  // Na kořeni (selectedPath = [], „Všechny stanice“) se barví podle kořenových uzlů stromu, protože selectedNode je null.
  const { colorByGhId: groupColorMap, legend: groupLegend } = useMemo(() => {
    if (selectedNode && selectedNode.children.length > 0) {
      return buildGroupColorMap(selectedNode.children)
    }
    if (!selectedNode && selectedPath.length === 0 && filteredTree.length > 0) {
      return buildGroupColorMap(filteredTree)
    }
    return { colorByGhId: null, legend: null }
  }, [selectedNode, selectedPath, filteredTree])

  const handleSelectSavedGroup = useCallback((value) => {
    if (!value) {
      resetToBlank()
      savedBaselineRef.current = null
      setBaselinePending(false)
      return
    }
    const g = savedGroups.find(sg => String(sg.id) === value)
    if (!g) return
    setGroupLevels(g.definition.groupLevels || [])
    setSelectedPath([])
    setGroupFilterBaseline(g.group_filter || {})
    setEditingSavedGroupId(g.id)
  }, [savedGroups, resetToBlank, setGroupLevels, setSelectedPath, setGroupFilterBaseline, setEditingSavedGroupId])

  const handleDeleteProductFilter = useCallback(async () => {
    await adminApi.deleteGroupProductFilter(editingSavedGroupId, activeProduct)
    setProductFilterBaseline(null)
    setFilters(groupFilterBaseline)
    qc.invalidateQueries({ queryKey: ['saved-station-group-filter', editingSavedGroupId, activeProduct] })
  }, [editingSavedGroupId, activeProduct, groupFilterBaseline, setProductFilterBaseline, setFilters, qc])

  const handleMapClick = useCallback(({ lat, lon }) => {
    if (localityPickMode) {
      setFilters({ ...filters, locality: { lat, lon } })
      setLocalityPickMode(false)
    }
  }, [localityPickMode, filters, setFilters])

  const activeFilterCount = [
    filters.ghId, filters.wsi, filters.name, filters.fullName,
    filters.stationTypes.length > 0, filters.elevationMin, filters.elevationMax,
    filters.hierarchyPaths.length > 0, filters.basin.length > 0, (filters.stationOwners?.length || 0) > 0,
    filters.elAbbreviations.length > 0,
    filters.locality, filters.plusGhId, filters.minusGhId,
  ].filter(Boolean).length
  const filtersSummary = activeFilterCount > 0 ? `${activeFilterCount} aktivních filtrů` : 'Bez filtrů'

  const editingGroup = editingSavedGroupId ? savedGroups.find(g => g.id === editingSavedGroupId) : null
  // Dirty-check pro banner „Upravujete...“ vynechává selectedPath záměrně — klik na uzel ve stromu
  // je jen navigace/prohlížení, ne úprava konfigurace skupiny (i když se selectedPath ukládá spolu
  // s definicí, aby si uložená skupina pamatovala naposledy vybraný uzel).
  const currentConfigJson = useMemo(() => JSON.stringify({ groupLevels, filters }), [groupLevels, filters])

  // Baseline se nastaví teprve tady — po renderu, kdy currentConfigJson už odráží state
  // aplikovaný v předchozím kroku (setGroupLevels/setSelectedPath/setFilters), ne o krok pozadu.
  useEffect(() => {
    if (!baselinePending) return
    savedBaselineRef.current = currentConfigJson
    setBaselinePending(false)
  }, [baselinePending, currentConfigJson])

  const hasUnsavedChanges = editingSavedGroupId != null && !baselinePending && savedBaselineRef.current !== null && savedBaselineRef.current !== currentConfigJson

  const panelTitle = expandedPanel === 'group' ? 'Skupina' : expandedPanel === 'filters' ? 'Filtry' : 'Produkt'

  return (
    <div className="flex h-dvh overflow-hidden bg-gray-50 relative">
      {/* Úzké levé menu — headery + trvalé souhrny, žádný scrollovatelný plný obsah */}
      <aside className="w-56 shrink-0 bg-white border-r border-gray-200 flex flex-col overflow-hidden z-20">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
          <button
            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 transition-colors flex-1 min-w-0"
            onClick={() => navigate('/')}
          >
            <span className="text-xl leading-none">🏠</span> Domů
          </button>
          <button
            className="flex items-center text-blue-600 hover:text-blue-700 transition-colors shrink-0"
            title="Výběr stanice"
            onClick={() => navigate('/select')}
          >
            <img src={weatherStationIcon} className="w-6 h-6" alt="Výběr stanice" />
          </button>
          <OnlineModeToggle />
          <UserIcon variant="compact" />
        </div>

        {/* Přepínač zobrazení vpravo: Mapa / Produkty */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setRightView('map')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors ${
              rightView === 'map' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-500' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            <img src={mapIcon} className="w-6 h-6" alt="" />
            Mapa
          </button>
          <button
            onClick={() => setRightView('products')}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-medium transition-colors ${
              rightView === 'products' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-500' : 'text-gray-500 hover:bg-gray-50'
            }`}
          >
            <img src={productsIcon} className="w-6 h-6" alt="" />
            Produkty
          </button>
        </div>

        {hasUnsavedChanges && (
          <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-800">
            ✏️ Upravujete uloženou skupinu „{editingGroup?.name ?? '…'}“ — změny se projeví ostatním až po uložení.
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto">
          <PanelHeader
            icon={groupIcon}
            label="Skupina"
            expanded={expandedPanel === 'group'}
            onToggle={() => setExpandedPanel(p => p === 'group' ? null : 'group')}
          />
          <GroupPathSelects
            groupLevels={groupLevels}
            filteredTree={filteredTree}
            selectedPath={selectedPath}
            setSelectedPath={setSelectedPath}
            savedGroups={savedGroups}
            editingSavedGroupId={editingSavedGroupId}
            onSelectSavedGroup={handleSelectSavedGroup}
          />

          <PanelHeader
            icon={productsIcon}
            label="Produkt"
            expanded={expandedPanel === 'products'}
            onToggle={() => setExpandedPanel(p => p === 'products' ? null : 'products')}
          />
          <div className="w-full px-3 py-2 border-b border-gray-100 text-xs">
            {activeProductGroup && (
              <div className="text-gray-400 mb-0.5">{activeProductGroup}</div>
            )}
            <select
              className="w-full border border-gray-200 rounded-lg px-2 py-1 text-xs"
              value={activeProduct ?? ''}
              onChange={e => e.target.value ? navigate(`/station-group/${e.target.value}`) : clearProduct()}
            >
              <option value="">— nevybráno —</option>
              {GROUP_PRODUCTS.map(g => (
                <optgroup key={g.group} label={g.group}>
                  {g.items.map(item => <option key={item.slug} value={item.slug}>{item.label}</option>)}
                </optgroup>
              ))}
            </select>
          </div>

          <PanelHeader
            icon={filterIcon}
            label="Filtry"
            expanded={expandedPanel === 'filters'}
            onToggle={() => setExpandedPanel(p => p === 'filters' ? null : 'filters')}
          />
          <InfoPanel
            label={null}
            summary={filtersSummary}
            trailing={isAdmin && productFilterBaseline !== null && (
              <button
                type="button"
                title="Smazat filtr k produktu"
                className="text-sm leading-none px-0.5 py-0.5 border border-red-300 text-red-600 hover:bg-red-50 transition-colors"
                onClick={handleDeleteProductFilter}
              >
                🗑️
              </button>
            )}
          />
        </div>
      </aside>

      {/* Rozbalený detail panel — NEpřekrývá mapu/produkty, ale je vsazený mezi levé menu a pravou
          stranu, takže se mapa/produkty jen odsunou doprava (flex-1 se zúží).
          Zavírá se JEN přes trojúhelník v headeru nebo ✕ — žádný click-outside handler. */}
      {expandedPanel && (
        <aside className="w-96 shrink-0 z-30 bg-white border-r border-gray-200 shadow-xl flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{panelTitle}</span>
            <button onClick={() => setExpandedPanel(null)} className="text-gray-400 hover:text-gray-700">✕</button>
          </div>
          <div className={`flex-1 min-h-0 flex flex-col ${expandedPanel === 'filters' ? '' : 'overflow-y-auto'}`}>
            {expandedPanel === 'group' && (
              <>
                <div className="shrink-0 flex flex-col gap-2 px-3 py-2">
                  {savedGroups.length > 0 && (
                    <select
                      className="border border-gray-200 rounded-lg px-2 py-1 text-xs"
                      value={editingSavedGroupId ?? ''}
                      onChange={e => handleSelectSavedGroup(e.target.value)}
                    >
                      <option value="">— vlastní výběr —</option>
                      {savedGroups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                    </select>
                  )}

                  {groupLevels.length === 0 ? (
                    <button
                      className="text-xs px-3 py-2 rounded-lg border border-blue-300 text-blue-600 hover:bg-blue-50"
                      onClick={() => setConfiguratorOpen(true)}
                    >
                      Nastav skupinu
                    </button>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        className="text-xs text-blue-600 underline"
                        onClick={() => setConfiguratorOpen(true)}
                      >
                        ✏️ Uprav skupiny
                      </button>
                      {isAdmin && (
                        <button
                          className="text-xs text-blue-600 underline"
                          onClick={() => setSaveModalOpen(true)}
                        >
                          💾 Uložit skupinu
                        </button>
                      )}
                    </div>
                  )}
                  {shrinkBanner && (
                    <div className="text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-2 py-1">
                      {shrinkBanner}
                    </div>
                  )}
                </div>
                {groupLevels.length > 0 && (
                  <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-2 flex flex-col gap-2">
                    <button
                      onClick={() => setSelectedPath([])}
                      className={`text-left text-xs py-0.5 px-1 rounded ${selectedPath.length === 0 ? 'bg-blue-100 text-blue-700 font-medium' : 'hover:font-medium hover:bg-gray-50'}`}
                    >
                      Všechny stanice
                    </button>
                    <GroupTree
                      tree={filteredTree}
                      selectedPath={selectedPath}
                      onSelect={setSelectedPath}
                    />
                  </div>
                )}
              </>
            )}

            {expandedPanel === 'filters' && (
              <FiltersPanel
                filters={filters}
                onChange={setFilters}
                onLocalityPickMode={setLocalityPickMode}
                localityPickMode={localityPickMode}
                isAdmin={isAdmin}
                editingGroupId={editingSavedGroupId}
                hasProductFilter={productFilterBaseline !== null}
                groupProducts={GROUP_PRODUCTS}
                activeProduct={activeProduct}
                onSaveToGroup={async () => {
                  const result = await adminApi.updateSavedStationGroup(editingSavedGroupId, { group_filter: filters })
                  setGroupFilterBaseline(result.group_filter || {})
                  qc.invalidateQueries({ queryKey: ['saved-station-groups'] })
                }}
                onSaveToProduct={async (productSlugs) => {
                  for (const slug of productSlugs) {
                    const result = await adminApi.saveGroupProductFilter(editingSavedGroupId, slug, filters)
                    if (slug === activeProduct) setProductFilterBaseline(result.filter)
                    qc.invalidateQueries({ queryKey: ['saved-station-group-filter', editingSavedGroupId, slug] })
                  }
                }}
                onDeleteProductFilter={handleDeleteProductFilter}
              />
            )}

            {expandedPanel === 'products' && (
              <div className="flex-1 min-h-0 overflow-y-auto px-3 py-2 flex flex-col gap-2">
                {GROUP_PRODUCTS.map(g => (
                  <div key={g.group}>
                    <div className="text-sm font-normal text-gray-600 mb-1">{g.group}</div>
                    <div className="flex flex-wrap gap-1 pl-1">
                      {g.items.map(item => (
                        <button
                          key={item.slug}
                          onClick={() => navigate(`/station-group/${item.slug}`)}
                          className={`text-xs px-2 py-1 rounded-full border ${
                            activeProduct === item.slug
                              ? 'bg-blue-500 border-blue-500 text-white'
                              : 'bg-white border-gray-300 text-gray-600 hover:border-blue-400'
                          }`}
                        >
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </aside>
      )}

      {/* Pravá strana — mapa nebo produkty, podle rightView (nezávisle na expandedPanel) */}
      <div className="flex-1 relative overflow-hidden">
        {(rightView === 'map' || activeProduct === null) && (
          <StationMap
            stations={effectiveStations}
            selectedStation={null}
            onStationSelect={(s) => navigate(`/station/${s.gh_id}`)}
            onMapClick={handleMapClick}
            groupColorMode
            groupColorMap={groupColorMap}
            groupLegend={groupLegend}
          />
        )}
        {rightView === 'products' && activeProduct && activeProductNode && (
          <activeProductNode.component
            selectedNode={selectedNode}
            selectedPath={selectedPath}
            stationsByGhId={stationsByGhId}
            filteredTree={filteredTree}
          />
        )}
      </div>

      {configuratorOpen && (
        <GroupConfigurator
          initialGroupLevels={groupLevels}
          onApply={(levels) => { setGroupLevels(levels); setConfiguratorOpen(false) }}
          onCancel={() => setConfiguratorOpen(false)}
        />
      )}

      {saveModalOpen && (
        <SaveGroupModal
          definition={{ groupLevels }}
          groupFilter={filters}
          editingGroup={editingGroup}
          onClose={(result) => {
            setSaveModalOpen(false)
            if (result) {
              setEditingSavedGroupId(result.id)
              setGroupFilterBaseline(result.group_filter || {})
              savedBaselineRef.current = currentConfigJson
            }
          }}
        />
      )}
    </div>
  )
}
